import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { BusinessService } from '../../../services/business.service';
import {
  BusinessCustomerDisplay,
  DISPLAY_LAYOUTS,
  DisplayLayoutInfo,
  layoutInfo,
  resolveDisplayLayout,
} from '../../../models/customer-display.model';
import { CustomerDisplayPreviewComponent } from './customer-display-preview.component';

/**
 * Plus.Admin — what this business's customers see on the till's second screen.
 * Read-only: merchants change it in POS → Devices → Customer Display. Shows the
 * layout the till actually draws (resolved by the till's own rule), the messages,
 * a preview, and every layout side by side for comparison.
 */
@Component({
  selector: 'app-customer-display-panel',
  standalone: true,
  imports: [DatePipe, CustomerDisplayPreviewComponent],
  templateUrl: './customer-display-panel.component.html',
  styleUrls: ['./customer-display-panel.component.css']
})
export class CustomerDisplayPanelComponent implements OnChanges {
  @Input() businessId?: string;
  /** The business type's name, which the till falls back to when no mode is set. */
  @Input() businessTypeName: string | null = null;

  settings: BusinessCustomerDisplay | null = null;
  loading = false;
  loadError: string | null = null;
  showAll = false;

  readonly layouts = DISPLAY_LAYOUTS;

  constructor(private businessService: BusinessService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessId'] && this.businessId) this.load(this.businessId);
  }

  /** The layout the till draws for this business. */
  get current(): DisplayLayoutInfo {
    return layoutInfo(resolveDisplayLayout(this.settings?.layout, this.settings?.modeKey, this.businessTypeName));
  }

  /** Where that layout comes from, in the merchant's own words. */
  get source(): string {
    if (this.settings?.layout) return 'Chosen by the business';
    return this.settings?.modeKey
      ? `Matches the operating mode (${this.settings.modeKey})`
      : `Matches the business type${this.businessTypeName ? ` (${this.businessTypeName})` : ''}`;
  }

  toggleAll(): void {
    this.showAll = !this.showAll;
  }

  private load(businessId: string): void {
    this.loading = true;
    this.loadError = null;
    this.businessService.getBusinessCustomerDisplay(businessId).subscribe({
      next: s => { this.settings = s; this.loading = false; },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.settings = null;
        this.loadError = err.status === 404
          ? 'This API does not have the customer display endpoint yet.'
          : 'Could not load the customer display settings.';
      }
    });
  }
}
